/**
* Copyright Zendesk, Inc.
*
* Use of this source code is governed under the Apache License, Version 2.0
* found at http://www.apache.org/licenses/LICENSE-2.0.
*/
import React, { useContext, useRef, useEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import mergeRefs from 'react-merge-refs';
import { useTooltip } from '@zendeskgarden/container-tooltip';
import { getControlledValue, composeEventHandlers } from '@zendeskgarden/container-utilities';
import { Manager, Reference, Popper } from 'react-popper';
import { getRtlPopperPlacement, getPopperPlacement } from '../utils/gardenPlacements.js';
import '../styled/StyledParagraph.js';
import '../styled/StyledTitle.js';
import { StyledTooltip } from '../styled/StyledTooltip.js';
import { StyledTooltipWrapper } from '../styled/StyledTooltipWrapper.js';
import { PLACEMENT, SIZE, TYPE } from '../types/index.js';

const Tooltip = _ref => {
  let {
    id,
    delayMS,
    isInitialVisible,
    content,
    refKey,
    placement,
    eventsEnabled,
    popperModifiers,
    children,
    hasArrow,
    size,
    type,
    appendToNode,
    zIndex,
    isVisible: externalIsVisible,
    ...otherProps
  } = _ref;
  const {
    rtl
  } = useContext(ThemeContext);
  const scheduleUpdateRef = useRef();
  const {
    isVisible,
    getTooltipProps,
    getTriggerProps,
    openTooltip,
    closeTooltip
  } = useTooltip({
    id,
    delayMilliseconds: delayMS,
    isVisible: isInitialVisible
  });
  const controlledIsVisible = getControlledValue(externalIsVisible, isVisible);
  useEffect(() => {
    if (controlledIsVisible && scheduleUpdateRef.current) {
      scheduleUpdateRef.current();
    }
  }, [controlledIsVisible, content]);
  const popperPlacement = rtl ? getRtlPopperPlacement(placement) : getPopperPlacement(placement);
  const singleChild = React.Children.only(children);
  const modifiers = {
    preventOverflow: {
      boundariesElement: 'window'
    },
    ...popperModifiers
  };
  return React.createElement(Manager, null, React.createElement(Reference, null, _ref2 => {
    let {
      ref
    } = _ref2;
    return cloneElement(singleChild, getTriggerProps({
      ...singleChild.props,
      [refKey]: mergeRefs([ref, singleChild.ref ? singleChild.ref : null])
    }));
  }), React.createElement(Popper, {
    placement: popperPlacement,
    eventsEnabled: controlledIsVisible && eventsEnabled,
    modifiers: modifiers
  }, _ref3 => {
    let {
      ref,
      style,
      scheduleUpdate,
      placement: currentPlacement
    } = _ref3;
    scheduleUpdateRef.current = scheduleUpdate;
    const {
      onFocus,
      onBlur,
      ...otherTooltipProps
    } = otherProps;
    let computedSize = size;
    if (computedSize === undefined) {
      if (type === 'dark') {
        computedSize = 'small';
      } else {
        computedSize = 'large';
      }
    }
    const tooltipProps = {
      hasArrow,
      placement: currentPlacement,
      size: computedSize,
      onFocus: composeEventHandlers(onFocus, () => {
        openTooltip();
      }),
      onBlur: composeEventHandlers(onBlur, () => {
        closeTooltip(0);
      }),
      'aria-hidden': !controlledIsVisible,
      type,
      ...otherTooltipProps
    };
    const tooltip = React.createElement(StyledTooltipWrapper, {
      ref: controlledIsVisible ? ref : null,
      style: style,
      zIndex: zIndex,
      "aria-hidden": !controlledIsVisible
    }, React.createElement(StyledTooltip, getTooltipProps(tooltipProps), content));
    if (appendToNode) {
      return createPortal(tooltip, appendToNode);
    }
    return tooltip;
  }));
};
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  appendToNode: PropTypes.any,
  hasArrow: PropTypes.bool,
  delayMS: PropTypes.number,
  eventsEnabled: PropTypes.bool,
  id: PropTypes.string,
  content: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(PLACEMENT),
  popperModifiers: PropTypes.any,
  size: PropTypes.oneOf(SIZE),
  type: PropTypes.oneOf(TYPE),
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isInitialVisible: PropTypes.bool,
  refKey: PropTypes.string
};
Tooltip.defaultProps = {
  hasArrow: true,
  eventsEnabled: true,
  type: 'dark',
  placement: 'top',
  delayMS: 500,
  refKey: 'ref'
};

export { Tooltip };
